import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";

import { Button, Select } from "../components/ui";
import { fetchErrorLogs } from "../utils";
import { ownerSelector } from "../store/store";

type ErrorLogRow = {
  _id: string;
  time: string | Date;
  channel: string;
  action: string;
  error: string;
  status: "Failed" | "Retrying" | "Skipped";
  route?: string;
  campaignId?: string | null;
};

const statusClass: Record<ErrorLogRow["status"], string> = {
  Failed: "bg-[#FDECEC] text-[#9F3A3A]",
  Retrying: "bg-[#FFF3D6] text-[#A06600]",
  Skipped: "bg-[#F0F2F5] text-[#5C6578]",
};

const formatTime = (value: string | Date | undefined) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

type ChannelFilter = "all" | "linkedin" | "email";
type StatusFilter = "all" | "failed" | "retrying" | "skipped";
type DayRangeFilter = "all" | "10" | "20" | "30";

export default function ErrorLogsPage() {
  const navigate = useNavigate();
  const { owner, currentWorkspace, currentUser } = useSelector(ownerSelector);
  const [rows, setRows] = React.useState<ErrorLogRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [channelFilter, setChannelFilter] = React.useState<ChannelFilter>("all");
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");
  const [dayRangeFilter, setDayRangeFilter] = React.useState<DayRangeFilter>("30");

  const handleExportPdf = React.useCallback(() => {
    if (!rows.length) return;

    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const marginLeft = 40;
    const marginTop = 48;
    const lineHeight = 18;
    const colWidths = [90, 100, 150, 230, 70];
    const columns = ["Time", "Channel", "Prospect / action", "Error", "Status"];

    pdf.setFillColor(245, 247, 250);
    pdf.rect(0, 0, pageWidth, 50, "F");
    pdf.setFontSize(18);
    pdf.setTextColor(17, 15, 42);
    pdf.text("Error log", marginLeft, 30);

    let x = marginLeft;
    let y = marginTop;

    pdf.setFontSize(9);
    pdf.setTextColor(90, 93, 110);
    pdf.setFont("helvetica", "bold");
    columns.forEach((col, index) => {
      pdf.text(col, x, y);
      x += colWidths[index];
    });

    y += 18;
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(30, 30, 30);

    rows.forEach((row) => {
      if (y > 760) {
        pdf.addPage();
        y = 48;
      }

      const status = row.status === "Failed" ? "Failed" : row.status === "Retrying" ? "Retrying" : "Skipped";
      const cells = [
        formatTime(row.time),
        row.channel,
        row.action,
        row.error.length > 80 ? `${row.error.slice(0, 77)}...` : row.error,
        status,
      ];

      x = marginLeft;
      cells.forEach((cell, index) => {
        pdf.text(String(cell || "—"), x, y, { maxWidth: colWidths[index] - 8 });
        x += colWidths[index];
      });

      y += lineHeight;
    });

    pdf.save("error-log.pdf");
  }, [rows]);

  React.useEffect(() => {
    let active = true;

    const load = async () => {
      if (!owner?._id && !currentWorkspace?._id) {
        setRows([]);
        return;
      }

      setLoading(true);
      try {
        const data = await fetchErrorLogs({
          ownerId: owner?._id,
          userId: currentUser?.userId,
          workspaceId: currentWorkspace?._id,
          limit: 50,
          page: 1,
          channel: channelFilter,
          status: statusFilter,
          days: dayRangeFilter === "all" ? "all" : Number(dayRangeFilter),
        });

        if (!active) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        setRows(
          items.map((item: any) => ({
            _id: item?._id || String(item?.time || Math.random()),
            time: item?.time || "",
            channel: String(item?.channel || item?.metadata?.channel || item?.category || "System"),
            action: String(item?.action || item?.metadata?.actionLabel || "Action"),
            error: String(item?.error || "No details recorded"),
            status: (() => {
            const rawStatus = String(item?.status || item?.metadata?.status || "skip").toLowerCase();
            const isRetrying =
              rawStatus === "retrying" ||
              item?.metadata?.retrying === true ||
              item?.metadata?.retryFlag === true ||
              item?.metadata?.isRetrying === true ||
              String(item?.metadata?.status || "").toLowerCase() === "retrying";
            if (rawStatus === "fail" && isRetrying) return "Retrying";
            if (rawStatus === "fail") return "Failed";
            return "Skipped";
          })(),
            route: item?.route || (item?.campaignId ? `/campaign-management/view/${item.campaignId}` : "/campaign-management"),
            campaignId: item?.campaignId || null,
          }))
        );
      } catch (error) {
        if (active) {
          setRows([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [owner?._id, currentWorkspace?._id, currentUser?.userId, channelFilter, statusFilter, dayRangeFilter]);

  return (
    <section className="h-full overflow-y-auto bg-paper px-4 py-6 sm:px-9 sm:py-9">
      <div className="mx-auto max-w-[1600px]">
        <header className="mb-6">
          <h1 className="text-xl font-semibold tracking-tight text-dark-blue sm:text-[27px]">
            Error log
          </h1>
          <p className="mt-1 text-sm text-muted">
            Review failed delivery attempts, provider errors, and actions that require attention.
          </p>
        </header>

        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
            <Select
              variant="compact"
              size="sm"
              ariaLabel="Filter by channel"
              value={channelFilter}
              onChange={(value) => setChannelFilter(value as ChannelFilter)}
              options={[
                { value: "all", label: "All channels" },
                { value: "linkedin", label: "LinkedIn" },
                { value: "email", label: "Email" },
              ]}
              className="w-full min-w-0 font-semibold text-dark-blue sm:!w-auto sm:min-w-[128px]"
            />
            <Select
              variant="compact"
              size="sm"
              ariaLabel="Filter by status"
              value={statusFilter}
              onChange={(value) => setStatusFilter(value as StatusFilter)}
              options={[
                { value: "all", label: "All statuses" },
                { value: "failed", label: "Failed" },
                { value: "retrying", label: "Retrying" },
                { value: "skipped", label: "Skipped" },
              ]}
              className="w-full min-w-0 font-semibold text-dark-blue sm:!w-auto sm:min-w-[130px]"
            />
            <Select
              variant="compact"
              size="sm"
              ariaLabel="Filter by day range"
              value={dayRangeFilter}
              onChange={(value) => setDayRangeFilter(value as DayRangeFilter)}
              options={[
                { value: "all", label: "All time" },
                { value: "10", label: "Last 10 days" },
                { value: "20", label: "Last 20 days" },
                { value: "30", label: "Last 30 days" },
              ]}
              className="w-full min-w-0 font-semibold text-dark-blue sm:!w-auto sm:min-w-[128px]"
            />
          </div>

          <Button
            variant="primary"
            size="sm"
            className="h-9 w-full border border-bright-green bg-bright-green px-3.5 text-sm font-semibold text-dark-blue disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            onClick={handleExportPdf}
            disabled={loading || rows.length === 0}
          >
            Export log
          </Button>
        </div>

        <p className="mb-2 text-[10px] text-muted md:hidden">Swipe horizontally to see all columns.</p>
        <div className="overflow-hidden rounded-[10px] border border-line bg-white">
          <div className="-mx-px overflow-x-auto px-px [scrollbar-width:thin]">
            <table className="min-w-[720px] w-full border-separate border-spacing-0 text-left">
              <thead className="bg-white">
                <tr>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.08em] text-muted">
                    Time
                  </th>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.08em] text-muted">
                    Channel
                  </th>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.08em] text-muted">
                    Prospect / action
                  </th>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.08em] text-muted">
                    Error
                  </th>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.08em] text-muted">
                    Status
                  </th>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.08em] text-muted text-right">
                    
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted">
                      Loading error log…
                    </td>
                  </tr>
                )}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted">
                      No failed or skipped actions recorded for this workspace yet.
                    </td>
                  </tr>
                )}

                {!loading && rows.map((row) => (
                  <tr key={row._id} className="border-t border-line align-top">
                    <td className="px-4 py-3 text-[12px] text-dark-blue">{formatTime(row.time)}</td>
                    <td className="px-4 py-3 text-[12px] font-medium text-dark-blue">{row.channel}</td>
                    <td className="px-4 py-3 text-[12px] font-medium text-dark-blue">{row.action}</td>
                    <td className="px-4 py-3 text-[12px] text-muted">{row.error}</td>
                    <td className="px-4 py-3">
                      <span
                        className={[
                          "inline-flex items-center rounded-full px-2 py-1 text-[10px] font-semibold",
                          statusClass[row.status],
                        ].join(" ")}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 border border-[#D9D9DE] bg-white px-2.5 text-[11px] font-semibold text-dark-black"
                        onClick={() => navigate(row.route || "/campaign-management")}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 rounded-[10px] border border-[#F5DAC8] bg-[#FFF8F1] px-4 py-3">
          <p className="text-sm font-semibold text-dark-blue">How errors are handled</p>
          <p className="mt-1 text-sm text-muted">
            Temporary provider failures can be retried automatically. Permanent delivery and
            configuration failures are recorded here and surfaced as live notifications.
          </p>
        </div>
      </div>
    </section>
  );
}
